import { useEffect, useRef, useState } from "react";
import {
  getSocket,
  subscribeTo,
  unsubscribeFrom,
  SOCKET_READY_EVENT,
  SOCKET_RESYNC_EVENT,
  type SocketRoomType,
} from "../services/socket";

/**
 * Re-renders whenever a new socket instance appears, so every hook below
 * re-binds to it. Covers both the child-effects-run-first ordering and the
 * logout → login case where the singleton is replaced. See SOCKET_READY_EVENT.
 */
const useSocketGeneration = () => {
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    const bump = () => setGeneration((n) => n + 1);
    window.addEventListener(SOCKET_READY_EVENT, bump);
    return () => window.removeEventListener(SOCKET_READY_EVENT, bump);
  }, []);

  return generation;
};

/**
 * Subscribe to a server event for the lifetime of a component.
 *
 * The handler is held in a ref so callers can pass an inline arrow function
 * without re-registering the listener on every render — the usual footgun with
 * hand-rolled `socket.on` / `socket.off` pairs.
 */
export const useSocketEvent = <T,>(
  event: string,
  handler: (payload: T) => void,
  enabled = true,
) => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const generation = useSocketGeneration();

  useEffect(() => {
    if (!enabled) return;

    const socket = getSocket();
    if (!socket) return;

    const listener = (payload: T) => handlerRef.current(payload);
    socket.on(event, listener);
    return () => {
      socket.off(event, listener);
    };
  }, [event, enabled, generation]);
};

/**
 * Whether the realtime channel is actually up right now.
 *
 * For the one thing a UI is entitled to say about it: a screen that advertises
 * itself as live has to stop saying so when it is not. A dot that is green
 * because the component rendered is worse than no dot at all — it is a promise
 * that messages will arrive, made at exactly the moment they will not.
 *
 * Tracks the socket's own connect/disconnect rather than a flag of our own, and
 * re-binds when the singleton is replaced (logout → login), same as
 * `useSocketEvent`.
 */
export const useSocketConnected = () => {
  const generation = useSocketGeneration();
  const [connected, setConnected] = useState(
    () => getSocket()?.connected ?? false,
  );

  useEffect(() => {
    const socket = getSocket();
    if (!socket) {
      setConnected(false);
      return;
    }

    // The socket may already be up by the time this binds — the events only
    // report transitions, so the current state has to be read once as well.
    setConnected(socket.connected);

    const up = () => setConnected(true);
    const down = () => setConnected(false);

    socket.on("connect", up);
    socket.on("disconnect", down);
    return () => {
      socket.off("connect", up);
      socket.off("disconnect", down);
    };
  }, [generation]);

  return connected;
};

/**
 * Join an entity room while a detail view is open, and leave on unmount.
 * Pass a falsy id (nothing open) to skip.
 */
export const useSocketRoom = (
  type: SocketRoomType,
  id: string | null | undefined,
) => {
  const generation = useSocketGeneration();

  useEffect(() => {
    if (!id) return;

    subscribeTo(type, id);

    // Reconnecting starts a fresh server-side session with no room memberships,
    // so the room has to be re-joined or the open detail view goes quietly deaf.
    const rejoin = () => subscribeTo(type, id);
    window.addEventListener(SOCKET_RESYNC_EVENT, rejoin);

    return () => {
      window.removeEventListener(SOCKET_RESYNC_EVENT, rejoin);
      unsubscribeFrom(type, id);
    };
  }, [type, id, generation]);
};

/**
 * Run a refetch whenever the socket reconnects. Events raised during the gap
 * are not replayed, so anything holding server state needs this.
 */
export const useSocketResync = (onResync: () => void, enabled = true) => {
  const handlerRef = useRef(onResync);
  handlerRef.current = onResync;

  useEffect(() => {
    if (!enabled) return;

    const listener = () => handlerRef.current();
    window.addEventListener(SOCKET_RESYNC_EVENT, listener);
    return () => window.removeEventListener(SOCKET_RESYNC_EVENT, listener);
  }, [enabled]);
};
